import React, { useCallback, useRef, useState } from "react";
import axios from "axios";
import { getUnexpiredToken, logAxiosError, useCanvasId } from "@/utils/utils";
import { ProjectRunMode } from "@/utils/templateProjects";
import { useSession } from "@clerk/clerk-react";

interface KubernetesTerminalProps {
  terminalContainerRef: React.MutableRefObject<HTMLDivElement | undefined>;
  parentContainer: React.MutableRefObject<HTMLDivElement | undefined>;
  previousTerminalRef: React.MutableRefObject<any>;
  terminalAddonRef: React.MutableRefObject<any>;
  project: any;
  terminalId?: string; // Add terminal ID parameter
}

export const useKubernetesTerminal = ({
  terminalContainerRef,
  parentContainer,
  previousTerminalRef,
  terminalAddonRef,
  project,
  terminalId = "default", // Default terminal ID
}: KubernetesTerminalProps) => {
  const [terminalInitialized, setTerminalInitialized] = useState(false);
  const [terminalInitializing, setTerminalInitializing] = useState(false);

  const canvasId = useCanvasId();
  const { session } = useSession();

  const cleanupPreviousTerminal = useCallback(() => {
    if (previousTerminalRef.current) {
      try {
        previousTerminalRef.current.dispose();
        previousTerminalRef.current = null;
        setTerminalInitialized(false);
      } catch (e) {
        console.error("Error cleaning up previous terminal:", e);
      }
    }
  }, []);

  const initTerminalKubernetes = useCallback(async () => {
    if (terminalInitializing) {
      return;
    }

    setTerminalInitializing(true);
    let terminal, resizeObserver, wsCleanupFunction;

    const cleanupFunction = () => {
      if (wsCleanupFunction) {
        wsCleanupFunction();
      }

      if (terminal) {
        terminal.dispose();
      }

      if (resizeObserver) {
        resizeObserver.disconnect();
      }

      setTerminalInitializing(false);
      setTerminalInitialized(false);
    };

    try {
      // Clean previous terminal before creating a new one
      cleanupPreviousTerminal();

      const { Terminal } = await import("@xterm/xterm");
      const { FitAddon } = await import("@xterm/addon-fit");

      if (!terminalContainerRef.current || !parentContainer.current) {
        setTerminalInitializing(false);
        return;
      }

      terminal = new Terminal({
        theme: {
          background: "#1a1b1d",
          foreground: "white",
          cursor: "#6448ef",
        },
        fontSize: 12,
        fontWeight: 500,
        cursorInactiveStyle: "outline",
        cursorBlink: true,
        allowTransparency: false,
        macOptionIsMeta: true,
        rightClickSelectsWord: true,
        convertEol: true,
        disableStdin: false,
        cols: 100, // Set explicit column count to prevent 80-character limit
      });
      previousTerminalRef.current = terminal;

      const fitAddon = new FitAddon();
      terminalAddonRef.current = fitAddon;

      terminal.loadAddon(fitAddon);
      terminal.open(terminalContainerRef.current);

      if (project?.runMode === ProjectRunMode.CLOUD) {
        const createWebSocketConnection = async () => {
          const getTerminalToken = async () => {
            const freshSupabaseJWT = await getUnexpiredToken(session);
            const terminalToken = (
              await axios.get(
                `${
                  import.meta.env.VITE_BACKEND_BASE_URL
                }/canvases/${canvasId}/websocketToken`,
                {
                  headers: {
                    Authorization: `Bearer ${freshSupabaseJWT}`,
                  },
                }
              )
            )?.data?.token;
            return terminalToken;
          };

          const terminalToken = await getTerminalToken();

          // Add terminal ID to WebSocket URL
          let socket = new WebSocket(
            `${
              import.meta.env.VITE_BACKEND_WS_BASE_URL
            }/canvases/${canvasId}/terminal?token=${terminalToken}&terminalId=${terminalId}`
          );

          let reconnectAttempts = 0;
          const maxReconnectAttempts = 5;
          let reconnectTimeout: NodeJS.Timeout;

          const setupSocketEventListeners = (ws: WebSocket) => {
            ws.addEventListener("open", () => {
              console.log(
                `Terminal WebSocket connected for terminal ${terminalId}`
              );
              reconnectAttempts = 0;
            });

            ws.addEventListener("close", (event) => {
              console.log(
                `Terminal WebSocket closed for terminal ${terminalId}:`,
                event.code,
                event.reason
              );
              if (event.code !== 1000) {
                handleReconnect();
              }
            });

            ws.addEventListener("error", (event) => {
              console.error(
                `Terminal WebSocket error for terminal ${terminalId}:`,
                event
              );
              handleReconnect();
            });

            ws.addEventListener("message", (event) => {
              terminal.write(event.data);
            });
          };

          const handleReconnect = () => {
            if (reconnectAttempts < maxReconnectAttempts) {
              reconnectAttempts++;
              console.log(
                `Terminal reconnect attempt ${reconnectAttempts} for terminal ${terminalId}`
              );

              reconnectTimeout = setTimeout(async () => {
                try {
                  // Close the current socket before creating a new one
                  if (socket && socket.readyState !== WebSocket.CLOSED) {
                    socket.close(1000, "Reconnecting");
                  }

                  // Get a fresh token for the new connection
                  const newTerminalToken = await getTerminalToken();

                  // Create new WebSocket connection with terminal ID
                  socket = new WebSocket(
                    `${
                      import.meta.env.VITE_BACKEND_WS_BASE_URL
                    }/canvases/${canvasId}/terminal?token=${newTerminalToken}&terminalId=${terminalId}`
                  );

                  // Setup event listeners for the new socket
                  setupSocketEventListeners(socket);
                } catch (error) {
                  console.error(
                    `Failed to reconnect terminal ${terminalId}:`,
                    error
                  );
                  // Try again if we haven't exceeded max attempts
                  if (reconnectAttempts < maxReconnectAttempts) {
                    handleReconnect();
                  }
                }
              }, 1000 * reconnectAttempts);
            }
          };

          // Setup initial event listeners
          setupSocketEventListeners(socket);

          terminal.onData((data) => {
            if (socket.readyState === WebSocket.OPEN) {
              socket.send(data);
              // Restore missing functionality from original
              if (data === "\r") {
                terminal.write("\r\n");
              }
            } else {
              console.warn(
                `Terminal WebSocket not open for terminal ${terminalId}, cannot send data`
              );
            }
          });

          terminal.onKey(({ key, domEvent }) => {
            if (domEvent.ctrlKey && domEvent.key === "c") {
              if (socket.readyState === WebSocket.OPEN) {
                socket.send("\x03");
              }
            }
          });

          // Handle page visibility changes
          const handleVisibilityChange = () => {
            if (!document.hidden && socket.readyState === WebSocket.CLOSED) {
              console.log(
                `Page became visible, checking terminal connection for terminal ${terminalId}`
              );
              handleReconnect();
            }
          };

          document.addEventListener("visibilitychange", handleVisibilityChange);

          return () => {
            if (reconnectTimeout) {
              clearTimeout(reconnectTimeout);
            }
            document.removeEventListener(
              "visibilitychange",
              handleVisibilityChange
            );
            if (socket && socket.readyState !== WebSocket.CLOSED) {
              socket.close(1000, "Component cleanup");
            }
          };
        };

        wsCleanupFunction = await createWebSocketConnection();
      }

      const resizeTerminal = () => {
        setTimeout(() => {
          if (terminalAddonRef.current) {
            terminalAddonRef.current.fit();
          }
        }, 10);
      };

      // Create a ResizeObserver
      resizeObserver = new ResizeObserver((entries) => {
        if (entries?.length) {
          resizeTerminal();
        }
      });

      if (parentContainer.current) {
        resizeObserver.observe(parentContainer.current);
      }

      // Initial resize
      resizeTerminal();

      setTerminalInitialized(true);
      setTerminalInitializing(false);
    } catch (e) {
      setTerminalInitializing(false);
      logAxiosError("Failed to initialize terminal", e);
    }

    return cleanupFunction;
  }, [
    cleanupPreviousTerminal,
    terminalInitializing,
    project?.runMode,
    session,
    canvasId,
    terminalId, // Add terminalId to dependencies
  ]);

  return {
    terminalInitialized,
    terminalInitializing,
    initTerminalKubernetes,
    cleanupPreviousTerminal,
  };
};
